import { Component, Inject, Injector, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Procedure } from '../../models/procedure.model';
import { ProcedureCreditedService } from '../../services/procedure-credited.service';
import { ProcedureCredited } from '../../models/procedure-credited.model';
import { ProcedureTrainingCredited } from '../../models/procedure-training-credited.model';
import { forkJoin, finalize, tap, of, map, catchError, switchMap } from 'rxjs';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';

@Component({
  selector: 'procedure-training-actions-dialog',
  templateUrl: './procedure-training-actions-dialog.component.html',
  styleUrls: ['./procedure-training-actions-dialog.component.scss']
})
export class ProcedureTrainingActionsDialogComponent extends BaseComponent implements OnInit {

  selectedTrainings: any[] = [];
  loading?: boolean = false;

  displayedColumns = ['procedure', 'title', 'date', 'action'];
  dataSource = new MatTableDataSource<any>();

  done = false;
  user?: User;

  constructor(
    protected override injector: Injector,
    private procedureCreditedService: ProcedureCreditedService,
    public dialogRef: MatDialogRef<ProcedureTrainingActionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      training: any;
    }
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.user = this.data.training.user;
    this.selectedTrainings = this.data.training.trainingsBeingUsed.filter((t: any) => t.selected);
    this.selectedTrainings.forEach((t: any) => {
      t.action = t.
        canCompleteAndApprove ? 'Approve & Complete' : t.canComplete ? 'Complete' : t.canApprove ? 'Approve' : 'N/A';
      t.procedureTrainingCredited = new ProcedureTrainingCredited();
      t.procedureTrainingCredited.creditDate = new Date();
    });
    this.dataSource = new MatTableDataSource(this.selectedTrainings);
  }

  close() {
    this.dialogRef.close();
  }

  confirm() {
    if (!this.selectedTrainings?.length) return;

    this.loading = true;

    const ops = this.selectedTrainings.map(t => this.runTrainingAction$(t));

    forkJoin(ops)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.done = true;
          this.alert.success('Actions completed successfully');
        },
        error: (err) => {
          // This should be rare because we catch errors per item,
          // but keep as a safety net.
          this.alert.defaultError();
          console.error(err);
        }
      });
  }

  private runTrainingAction$(t: any) {
    // If canCompleteAndApprove means "do both", implement it here:
    if (t.canCompleteAndApprove) {
      return this.complete$(t).pipe(
        switchMap((completedOk) => completedOk ? this.approve$(t) : of(false)),
        map((approvedOk) => {
          if (approvedOk) {
            t.action = 'Approved & Completed';
            t.done = true;
          }
          else if (t.action !== 'Error') { // if it was already set to Error, keep it as is
            t.action = 'Warning';
            t.toolTip = "Completed — self-approval is not allowed"
          }
          return approvedOk;
        }) // final result = approve result
      );
    }

    if (t.canComplete) return this.complete$(t);
    if (t.canApprove) return this.approve$(t);

    return of(null); // nothing to do
  }

  private complete$(t: any) {
    return this.procedureCreditedService.complete(t.procedure.id, this.user?.id).pipe(
      tap((pc) => {
        t.action = 'Completed';
        t.done = true;
        t.procedureCredited = pc;
      }),
      map((pc) => true),
      catchError(err => {
        this.alert.defaultError();
        console.error(err);
        t.action = 'Error';
        return of(false);
      })
    );
  }

  private approve$(t: any) {
    const procedureCredited = t.procedureCredited as ProcedureCredited;

    if (!procedureCredited?.id) return of(false);

    return this.procedureCreditedService.approve(procedureCredited.id, t.procedureTrainingCredited).pipe(
      tap(() => {
        t.action = t.action == 'Complete' ? 'Completed & Approved' : 'Approved';
        t.done = true;
      }),
      map(() => true),
      catchError(err => {
        if (err.status === 404 || err.status === 409) {
          this.alert.error(err.error);
        } else {
          this.alert.defaultError();
        }
        console.error(err);
        t.action = 'Error';
        return of(false);
      })
    );
  }

}
